import { DateTime } from 'luxon';
import React from 'react';
import _ from 'lodash';
import { useBinLevelMeasurementOverrides, useMeasurement, sirenActions, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import {
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Icons,
  alpha,
} from '~/lib/bn-material-ui';
import { useStateValue } from './data/order-calculator-context';
import { useOrderForecast } from './data/order-forecast-helpers';
import { getOrderStatusText, OrderStatusIcon } from '~/orders/order-status';
import OrderForm from './order-form';
import OrderButtons from './order-buttons';
import { useBinVolumeGetter } from './data/bin-level-helpers';
import { BinLevelProfile } from '~/bins/bin-level-profile';
import { translateNumber, useTranslation } from '~/translations';

const DATE_FORMAT = 'yyyy-MM-dd\'T\'HH:mm';

const useStyles = makeStyles(theme => {
  return {
    // when no order is displayed
    infoText: {
      margin: '5rem 1rem 1rem',
      '& > p': {
        textAlign: 'center',
      },
    },

    // when order is displayed
    container: {
      border: `1px solid ${theme.palette.gray[40]}`,
      background: 'white',
      margin: theme.spacing(2),
    },
    content: {
      display: 'grid',
      gridTemplateColumns: 'minmax(60px, 150px) auto',
      gridTemplateRows: '1fr',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },

    // bin level
    binProfile: {
      gridColumn: '1',
      gridRow: '1/3',
      maxWidth: '150px',
      minWidth: '60px',
      maxHeight: '225px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.down('sm')]: {
        gridRow: '1',
      },
    },
    unmonitoredBinLevel: {
      width: '67px',
      height: '100%',
    },

    // order details
    details: {
      gridRow: '1',
      gridColumn: '2',
    },
    feedLevelLabel: {
      display: 'flex',
      alignItems: 'center',
    },
    legend: {
      height: '2ch',
      width: '2ch',
      background: theme.palette.primary.main,
      marginLeft: '1ch',
      borderRadius: '0.25ch',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '40% 60%',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '100%',
      },
    },
    cols: {
      display: 'flex',
      flexDirection: 'column',
      alignContent: 'space-between',
      '& > div': {
        marginBottom: theme.spacing(2),
        flex: 1,
      },
    },
    date: {
      fontWeight: 'bold',
      fontSize: '1.1em',
    },
    feedLevelAmount: {
      fontWeight: 'bold',
    },
    footer: {
      width: '100%',
      gridRow: '2',
      gridColumn: '2',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      borderTop: `1px solid ${theme.palette.gray[40]}`,
      [theme.breakpoints.down('sm')]: {
        gridColumn: '1/3',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    warning: {
      flexGrow: '1',
    },
    buttons: {
      '& > button': {
        margin: theme.spacing(1),
        marginBottom: 0,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      },
    },
  };
});

export default function Order({ bin, stats }) {
  const styles = useStyles();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useStateValue();
  const { entity: order, loading: orderLoading } = useSirenEntity({ href: state.activeOrderId });
  const { loading: ordersEntityLoading } = useSirenEntity({ href: state.ordersHref });
  const { volumeMap: orderForecastVolumes, loading: loadingOrderForecast } = useOrderForecast(stats, state.activeOrderId, 0);
  const { entity: binLevelsBase } = useSirenEntity({ href: bin.getSubEntity('https://api.binsentry.com/rel/v2/bin-levels').href });
  const { entity: binLevelLatest } = useSirenEntity({ href: bin.getSubEntity('https://api.binsentry.com/rel/bin-level-latest').href });
  const binVolumeGetter = useBinVolumeGetter();
  const measurementOverrides = useBinLevelMeasurementOverrides({ bin });
  const binLevelMeasurement = useMeasurement({ name: 'binLevel', ...measurementOverrides });
  const orderAmountMeasurement = useMeasurement({ name: 'orderAmount' });

  const originalFields = {
    deliveryDate: order?.properties.deliveryDate,
    orderQuantity: order?.properties.quantity || 0,
    feedId: order?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href,
    medicated: order?.properties.medicated || false,
  };
  const fields = {
    deliveryDate: state.activeOrder.deliveryDate === undefined ? originalFields.deliveryDate : state.activeOrder.deliveryDate,
    orderQuantity: state.activeOrder.orderQuantity === undefined ? originalFields.orderQuantity : state.activeOrder.orderQuantity,
    feedId: state.activeOrder.feedId === undefined ? originalFields.feedId : state.activeOrder.feedId,
    medicated: state.activeOrder.medicated === undefined ? originalFields.medicated : state.activeOrder.medicated,
  };
  const { entity: feed, loading: feedLoading } = useSirenEntity({ href: fields.feedId });

  const localDate = DateTime.fromISO(fields.deliveryDate).setLocale(i18n.language);

  const { entity: binLevels } = useSirenEntity({ href: binLevelsHref(binLevelsBase?.getAction('custom-date-range'), localDate) });

  if (!fields.deliveryDate) {
    return (
      <div className={styles.infoText}>
        <p>{t('order.selectDeliveryDate')}</p>
        <p>{t('order.or')}</p>
        <p>{t('order.selectExistingOrder')}</p>
      </div>
    );
  }

  const systemOrderQuantity = fields.orderQuantity;
  const bulkDensity = feed?.properties?.bulkDensity || 0;
  // still load the page, but show 0 values while waiting.  This prevents distracting form flashes when switching orders.
  const loadingOrderDeps = loadingOrderForecast || feedLoading || orderLoading || ordersEntityLoading;
  const orderQuantity = loadingOrderDeps ? 0 : orderAmountMeasurement.convert({
    volume: systemOrderQuantity,
    bulkDensity: bulkDensity,
  });

  const binVolume = getBinVolume(fields.deliveryDate, binLevels, binLevelLatest, stats, binVolumeGetter);

  const currentFeedLevel = binVolume === undefined || !bin ? undefined : binLevelMeasurement.convert({
    volume: binVolume,
    bulkDensity: bin.properties?.bulkDensity,
    totalVolume: bin.properties?.volumes.fullTotal,
  });

  const forecastedOrderQuantity = (orderForecastVolumes.get(localDate.toISODate()) || {})[Math.floor(localDate.hour / 4)]?.volume || 0;

  const maxBinLevel = loadingOrderDeps ? 0 : orderAmountMeasurement.convert({
    volume: bin.properties.volumes.fullTotal - binVolume - forecastedOrderQuantity,
    bulkDensity,
  });

  const overfillPredicted = localDate > DateTime.now() && orderQuantity > maxBinLevel && !loadingOrderDeps;

  const orderAmount = forecastedOrderQuantity + ((state.activeOrderId && orderQuantity > 0) ? systemOrderQuantity : 0);
  const volumes = [
    binVolume,
    ...(orderAmount && [orderAmount]) || [],
  ];

  const ordersForDay = !loadingOrderDeps && state.orders?.[localDate.toISODate()] || [];
  const onFeedChange = (feedId) => dispatch({ type: 'selectFeedId', feedId });

  function deliveryDateIsValid() {
    return localDate >= DateTime.now().startOf('day');
  }

  return (
    <Paper className={styles.container} variant='outlined'>
      <OrderToolbar orders={ordersForDay} overfillPredicted={overfillPredicted} deliveryDate={fields.deliveryDate} />

      <div className={styles.content}>
        <div className={styles.binProfile}>
          <BinLevelProfile
            binHref={bin?.getLink('self')?.href}
            heightPx={225}
            widthPx={150}
            feeds={[
              { volume: volumes[0], color: theme.palette.primary.main },
              ...volumes[1] ? [{ volume: volumes[1], color: alpha(theme.palette.lavender[60], 0.5) }] : [],
            ]}
          />
        </div>

        <div className={styles.details}>
          <div className={styles.row}>
            <div className={styles.cols}>
              <div>
                {state.editingOrder ? (
                  <TextField
                    error={!deliveryDateIsValid()}
                    type='datetime-local'
                    value={localDate.toFormat(DATE_FORMAT)}
                    helperText={deliveryDateIsValid() ? '' : t('order.dateCannotBeInPast')}
                    onChange={e => dispatch({ type: 'setDeliveryDate', deliveryDate: e.target.value })}
                  />
                ) : (
                  <>
                    <div className={styles.date}>
                      {localDate.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className={styles.time}>
                      {localDate.toLocaleString({ hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </>
                )}
              </div>
              <div>
                <div className={styles.feedLevelLabel}>
                  {t('order.feedLevel')}
                  <span className={styles.legend} />
                </div>
                <div className={styles.feedLevelAmount}>
                  {t('numberWithUntranslatedUnit', {
                    number: currentFeedLevel === undefined ? '-' : translateNumber(parseFloat(currentFeedLevel.toFixed(2)), i18n.language),
                    unit: binLevelMeasurement.unit,
                  })}
                </div>
              </div>
            </div>

            {(order || state.editingOrder) &&
              <OrderForm
                setOrderQuantity={(orderQuantity) => {
                  const systemOrderQuantity = bulkDensity > 0 && orderQuantity > 0
                    ? orderAmountMeasurement.reverse({ volume: parseFloat(orderQuantity), weight: parseFloat(orderQuantity), bulkDensity })
                    : 0;
                  dispatch({ type: 'setOrderQuantity', orderQuantity, systemOrderQuantity });
                }}
                orderQuantity={orderQuantity}
                orderUnit={orderAmountMeasurement.unit}
                maxBinLevel={maxBinLevel}
                defaultFeedId={fields.feedId}
                onFeedChange={onFeedChange}
                medicated={fields.medicated}
                onMedicatedChange={medicated => dispatch({ type: 'setMedicated', medicated })}
                readOnly={!state.editingOrder}
                bin={bin}
              />
            }
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.warning}>
            {overfillPredicted && <OverfillWarning />}
          </div>
          <div className={styles.buttons}>
            <OrderButtons orderEntity={order} binEntity={bin} />
          </div>
        </div>
      </div>
    </Paper>
  );
}

const useOrderToolbarStyles = makeStyles(theme => {
  return {
    toolbar: {
      display: 'flex',
      borderBottom: `1px solid ${theme.palette.gray[40]}`,
      height: '3em',
    },
    toolbarInfo: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    orderStatusText: {
      marginLeft: '1em',
      fontWeight: 'bold',
    },
    iconContainer: {
      width: '3em',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > *': {
        color: theme.palette.indigo[60],
      },
    },
  };
});

function OrderToolbar({ orders, overfillPredicted, deliveryDate }) {
  const styles = useOrderToolbarStyles();
  const { t } = useTranslation();
  const [{ activeOrderId }, dispatch] = useStateValue();
  const { entity: order } = useSirenEntity({ href: activeOrderId });
  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarInfo} >
        {order ? (
          <>
            <div className={styles.iconContainer}>
              <OrderStatusIcon order={order} hasOverFill={overfillPredicted}/>
            </div>
            <span className={styles.orderStatusText}>
              {getOrderStatusText({ order, deliveryDate })}
            </span>
          </>
        ) : (
          <span className={styles.orderStatusText}>
            {t('order.createOrder')}
          </span>
        )}
        {orders.length > 1 && <OtherOrderSelector orders={orders} />}
      </div>
      <IconButton
        edge='start'
        color='inherit'
        onClick={() => dispatch({ type: 'deselectOrder' })}
        aria-label='close'
        size='large'>
        <Icons.Close />
      </IconButton>
    </div>
  );
}

const useOtherOrderSelectorStyles = makeStyles(() => {
  return {
    selectRoot: {
      marginLeft: '3em',
      paddingLeft: '0.3em',
    },
  };
});

function OtherOrderSelector({ orders }) {
  const styles = useOtherOrderSelectorStyles();
  const { t } = useTranslation();
  const [{ activeOrderId }, dispatch] = useStateValue();
  return (
    <Select
      classes={styles.selectRoot}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        getContentAnchorEl: null,
      }}
      disableUnderline
      displayEmpty
      renderValue={() => t('order.otherOrders')}
      value={activeOrderId}
      onChange={event => dispatch({ type: 'selectOrder', activeOrderId: event.target.value })}
    >
      {orders.map(order => {
        const orderId = order.getLink('self').href;
        return (
          <MenuItem key={orderId} value={orderId}>
            {getMenuItemText(order)}
          </MenuItem>
        );
      })}
    </Select>
  );
}

const useOverfillStyles = makeStyles((theme) => {
  return {
    overfillPredicted: {
      margin: '0.5rem auto 0rem 0',
      backgroundColor: theme.palette.yellow[10],
      padding: '0.5rem 0.75rem',
      borderRadius: '100px',
      userSelect: 'none',
      color: theme.palette.yellow[30],
    },
    overfillPopoverText: {
      maxWidth: '300px',
    },
    overfillIcon: {
      marginBottom: '-0.25rem',
    },
  };
});

function OverfillWarning() {
  const styles = useOverfillStyles();
  const { t } = useTranslation();
  return (
    <div className={styles.overfillPredicted}>
      <Tooltip title={
        <div className={styles.overfillPopoverText}>
          {t('order.overfillWarningText')}
        </div>
      }>
        <Typography variant='subtitle2' component='span'>
          <Icons.WarningRounded className={styles.overfillIcon}/> {t('order.overfillPredicted')}
        </Typography>
      </Tooltip>
    </div>
  );
}

function binLevelsHref(binLevelsDateRangeAction, deliveryDateTime) {
  if (deliveryDateTime > DateTime.now() || !binLevelsDateRangeAction) {
    return; // use stats for dates in the future
  }
  return sirenActions.parseAction(binLevelsDateRangeAction, {
    startDate: deliveryDateTime.minus({ day: 1 }).toUTC().toISO(),
    endDate: deliveryDateTime.minus({ millisecond: 1 }).toUTC().toISO(),
  })?.url?.href;
}

function getBinVolume(deliveryDate, binLevels, binLevelLatest, stats, binVolumeGetter) {
  if (!deliveryDate) {
    return;
  }
  const date = DateTime.fromISO(deliveryDate);
  if (date < DateTime.now()) {
    // recall these bin levels are sorted by *descending* date so the first is the latest
    return binVolumeGetter(binLevels?.entities[0]);
  }
  const forecast = stats?.properties.volume.forecast;
  if (!forecast || !forecast.length || deliveryDate < forecast[0].publishedAt) {
    return binVolumeGetter(binLevelLatest);
  }
  const prediction = _.findLast(forecast, data => data.publishedAt <= deliveryDate);
  return prediction?.low;
}

function getMenuItemText(order) {
  const status = getOrderStatusText({ order });
  const deliveryDateTime = DateTime.fromISO(order.properties.deliveryDate);
  return `${status} - ${deliveryDateTime.toFormat('h:mm a')}`;
}
